import {
    BillingPeriodId,
    BillingPeriodIdMap,
    defaultBillingPeriodId,
    defaultCommitmentPeriodId,
    defaultCommitmentPeriods,
    defaultMembershipTierId,
    defaultTrialPeriodId,
    defaultTrialPeriods,
    isNoTrial,
    TrialId,
} from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { addMonths, format, parseISO } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { tenYearsEndMonth } from "../../../date-time-picker/DateTimePicker";
import { useMemberProfile } from "../../MemberProfileContext";
import { DateField, OwnedField } from "../FormFields";
import { FormShell } from "../ProfileOverlayShell";
import type { FormProps } from "../types";

const editMembershipSchema = z.object({
    tier: z.string(),
    trialPeriod: z.string(),
    commitmentPeriod: z.string(),
    billingPeriod: z.string(),
});

type EditFormValues = z.infer<typeof editMembershipSchema>;

export function EditMembershipForm({
    mode = "edit",
    onClose,
}: FormProps & {
    mode?: "create" | "edit";
}) {
    const { profile, membershipTiers, saveMembership } = useMemberProfile();
    const creating = mode === "create";
    const { control, handleSubmit, watch } = useForm<EditFormValues>({
        resolver: zodResolver(editMembershipSchema),
        defaultValues: {
            tier: profile.membership.tier ?? defaultMembershipTierId,
            trialPeriod: creating
                ? defaultTrialPeriodId
                : (profile.membership.trialPeriod ??
                  (profile.membership.trial ? defaultTrialPeriodId : "no-trial")),
            commitmentPeriod:
                profile.membership.commitmentPeriod ?? defaultCommitmentPeriodId,
            billingPeriod: profile.membership.billingPeriod ?? defaultBillingPeriodId,
        },
    });
    const [startDate, setStartDate] = useState(
        creating
            ? new Date()
            : profile.membership.startsOn
              ? parseISO(profile.membership.startsOn)
              : new Date(),
    );
    const trialPeriod = watch("trialPeriod");
    const commitmentPeriod = watch("commitmentPeriod");
    const trial = TrialId.safeParse(trialPeriod);
    const paid = trial.success && isNoTrial(trial.data);
    const endDate = paid
        ? addMonths(
              startDate,
              defaultCommitmentPeriods.find((period) => period.id === commitmentPeriod)
                  ?.value ?? 0,
          )
        : addMonths(
              startDate,
              defaultTrialPeriods.find((period) => period.id === trialPeriod)?.value ??
                  0,
          );

    return (
        <FormShell
            title="Edit membership"
            onClose={onClose}
            saveLabel={creating ? "Update membership" : "Save"}
            saveDisabled={!startDate}
            onSave={handleSubmit((values) => {
                if (!startDate) return;
                const parsedTrial = TrialId.safeParse(values.trialPeriod);
                if (!parsedTrial.success) return;
                const start = format(startDate, "yyyy-MM-dd");
                const end = format(endDate, "yyyy-MM-dd");
                const paidMembership = isNoTrial(parsedTrial.data);

                saveMembership(
                    {
                        membership: paidMembership
                            ? {
                                  tier: values.tier,
                                  trial: parsedTrial.data,
                                  startDate: start,
                                  endDate: end,
                                  startTrialDate: null,
                                  endTrialDate: null,
                                  commitmentPeriod: values.commitmentPeriod,
                                  billingPeriod: BillingPeriodId.parse(
                                      values.billingPeriod,
                                  ),
                                  cancellation: null,
                              }
                            : {
                                  tier: values.tier,
                                  trial: parsedTrial.data,
                                  startTrialDate: start,
                                  endTrialDate: end,
                                  startDate: null,
                                  endDate: null,
                                  commitmentPeriod: null,
                                  billingPeriod: null,
                                  cancellation: null,
                              },
                    },
                    onClose,
                );
            })}
        >
            <OwnedField
                id="edit-tier"
                label="Tier"
                kind="select"
                options={membershipTiers.map((tier) => ({
                    id: tier.id,
                    label: tier.name,
                }))}
                control={control}
                name="tier"
            />
            <OwnedField
                id="edit-trial"
                label="Trial"
                kind="select"
                options={defaultTrialPeriods.map((period) => ({
                    id: period.id,
                    label: period.name,
                }))}
                control={control}
                name="trialPeriod"
            />
            {paid && (
                <>
                    <OwnedField
                        id="edit-commitment"
                        label="Commitment period"
                        kind="select"
                        options={defaultCommitmentPeriods.map((period) => ({
                            id: period.id,
                            label: period.name,
                        }))}
                        control={control}
                        name="commitmentPeriod"
                    />
                    <OwnedField
                        id="edit-billing"
                        label="Billing period"
                        kind="select"
                        options={BillingPeriodId.options.map((id) => ({
                            id,
                            label: BillingPeriodIdMap[id],
                        }))}
                        control={control}
                        name="billingPeriod"
                    />
                </>
            )}
            <DateField
                id="edit-start"
                label={paid ? "Membership start date" : "Trial start date"}
                date={startDate}
                endMonth={tenYearsEndMonth()}
                onDateChange={(date) => {
                    if (date) setStartDate(date);
                }}
            />
            <DateField
                id="edit-end"
                label={paid ? "Membership renewal date" : "Trial end date"}
                date={endDate}
                disabled
            />
        </FormShell>
    );
}
